import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import Spinner from '../components/Spinner';

const StudentFormPage = ({ darkMode, user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [error, setError] = useState('');
  const [successStatus, setSuccessStatus] = useState(false);
  const [successAppId, setSuccessAppId] = useState('');

  const [formData, setFormData] = useState({
    name: user?.displayName || '',
    email: user?.email || '',
    phone: '',
    neetScore: '',
    category: '', // Caste
  });

  // Ensure email and name are populated if user changes
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        email: prev.email || user.email || '',
        name: prev.name || user.displayName || '',
      }));

      const checkExistingApplication = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/admin/students`);
          if (response.data.success && response.data.students) {
            const hasApplied = response.data.students.some(student => student.email === user.email);
            if (hasApplied) {
              navigate('/status-check', { replace: true });
            } else {
              setCheckingStatus(false);
            }
          } else {
            setCheckingStatus(false);
          }
        } catch (err) {
          console.error('Failed to check existing application:', err);
          setCheckingStatus(false);
        }
      };

      checkExistingApplication();
    } else {
      // If no user object, maybe let them fill the form anyway
      setCheckingStatus(false);
    }
  }, [user, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Extra manual validation
    if (!formData.name || !formData.phone || !formData.neetScore || !formData.category) {
      setError('Please fill in all the required fields.');
      setLoading(false);
      return;
    }

    try {
      // Provide defaults for fields to keep the backend safe and backward-compatible
      const studentData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone.startsWith('+91') ? formData.phone : `+91 ${formData.phone.trim()}`,
        address: 'Quick Apply - N/A',
        city: 'Quick Apply - N/A',
        pincode: '000000',
        dateOfBirth: '2000-01-01',
        gender: 'Not Specified',
        category: formData.category,
        neetScore: parseInt(formData.neetScore) || 0,
        neetEligibility: 'Yes',
        neetApplicationNumber: 'N/A',
        desiredCollege: 'To Be Decided',
        desiredCountry: 'To Be Decided',
        files: {
          photo: 'https://res.cloudinary.com/demo/image/upload/v1312461204/sample.jpg',
          marksheet10: 'https://res.cloudinary.com/demo/image/upload/v1312461204/sample.jpg',
          marksheet12: 'https://res.cloudinary.com/demo/image/upload/v1312461204/sample.jpg',
          neetScoreCard: 'https://res.cloudinary.com/demo/image/upload/v1312461204/sample.jpg',
          aadharCard: 'https://res.cloudinary.com/demo/image/upload/v1312461204/sample.jpg'
        },
        userId: user?.uid,
      };

      try {
        let response;
        // First try to post as a new application
        try {
          response = await axios.post(`${API_BASE_URL}/api/students/register`, studentData, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000
          });
        } catch (registerError) {
          // If the error indicates the student already exists, fetch their ID and update instead
          const errMsg = registerError.response?.data?.message || '';
          
          // Also try to update if it's a generic network error (e.g. from an old Service Worker crashing on a 400 response)
          const isNetworkError = registerError.message === 'Network Error' || registerError.message.includes('fetch');
          
          if (errMsg.includes('already exists') || registerError.response?.status === 400 || isNetworkError) {
            console.log('User might already exist (or network error occurred), attempting to find and update existing application...');
            
            // Try to fetch existing user data by email to get applicationId
            const allStudentsResponse = await axios.get(`${API_BASE_URL}/api/admin/students`, { timeout: 30000 });
            const allStudents = allStudentsResponse.data?.data || allStudentsResponse.data?.students || [];
            const existingStudent = allStudents.find(s => s.email === formData.email || s.phone === formData.contactNumber);
            
            if (existingStudent && existingStudent.applicationId) {
              console.log('Found existing application, updating:', existingStudent.applicationId);
              studentData.applicationId = existingStudent.applicationId;
              studentData.isUpdate = true;
              
              response = await axios.put(`${API_BASE_URL}/api/students/update/${existingStudent.applicationId}`, studentData, {
                headers: { 'Content-Type': 'application/json' },
                timeout: 30000
              });
            } else {
              throw registerError; // If we can't find them, throw the original error
            }
          } else {
            throw registerError; // If it's a different error, throw it
          }
        }

        if (response.data.success) {
          const appId = response.data.applicationId || response.data.student?.applicationId || studentData.applicationId || '';
          setSuccessAppId(appId);
          setSuccessStatus(true);
          setTimeout(() => {
            navigate('/status-check');
          }, 3000);
        }
      } catch (err) {
        console.error('Submission error:', err);
        const errMsg = err.response?.data?.message || 'Failed to submit application. Please try again.';
        setError(errMsg);
      }
    } catch (err) {
      console.error('Submission error:', err);
      const errMsg = err.response?.data?.message || 'Failed to submit application. Please try again.';
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className={`min-h-[calc(100vh-80px)] flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-[#070B14]'}`}>
        <Spinner size="lg" color="white" />
        <p className="ml-4 text-white text-lg animate-pulse">Checking your application status...</p>
      </div>
    );
  }

  if (successStatus) {
    return (
      <div className={`min-h-[calc(100vh-80px)] ${darkMode ? 'bg-gray-900' : 'bg-[#070B14]'} flex items-center justify-center p-4`}>
        <div className="bg-[#0D1425]/80 backdrop-blur-xl border border-white/10 p-8 rounded-2xl shadow-2xl text-center max-w-lg w-full">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">Application Submitted!</h2>
          {successAppId && (
            <div className="mb-6 p-4 bg-[#1A233A] rounded-xl border border-white/10">
              <p className="text-blue-200 text-sm mb-1">Your Application ID</p>
              <p className="text-2xl font-mono font-bold text-white tracking-wider">{successAppId}</p>
            </div>
          )}
          <p className="text-blue-200 text-lg mb-6">We have received your details safely.</p>
          <p className="text-white/60 text-sm">Redirecting you to status check...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-[calc(100vh-80px)] relative overflow-hidden pb-20 font-sans ${darkMode ? 'bg-gray-900' : 'bg-[#070B14]'}`}>
      <div className="relative pt-12 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="max-w-2xl mx-auto mt-8">
          
          <div className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
                Apply Now
              </span>
            </h1>
            <p className="text-lg text-blue-200">
              Provide these basic details to start your application
            </p>
          </div>

          {/* User Info Card */}
          {user && (
            <div className="mb-8 bg-[#0D1425]/60 border border-white/10 rounded-2xl p-4 shadow-xl flex items-center justify-between backdrop-blur-md">
              <div className="flex items-center space-x-4">
                {user.photoURL ? (
                  <img src={user.photoURL} alt="Profile" className="w-12 h-12 rounded-full border-2 border-blue-500/50" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-blue-500/20 border-2 border-blue-500 flex items-center justify-center">
                    <span className="text-xl text-blue-200">{user.email?.charAt(0).toUpperCase() || '?'}</span>
                  </div>
                )}
                <div>
                  <p className="text-white/60 text-xs sm:text-sm">Signed in as</p>
                  <p className="text-white font-medium break-all text-sm sm:text-base">{user.email}</p>
                </div>
              </div>
              <button
                onClick={onLogout}
                className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors text-sm border border-red-500/20"
              >
                Sign Out
              </button>
            </div>
          )}

          <div className="bg-[#0D1425]/80 backdrop-blur-xl border border-white/10 p-6 sm:p-10 rounded-2xl shadow-2xl">
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 flex items-start gap-3">
                <svg className="w-6 h-6 flex-shrink-0 mt-0.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p>{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* 1. Name */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">Student Name <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full bg-[#1A233A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                  placeholder="Enter your full name"
                  required
                />
              </div>

              {/* 2. Email (Readonly) */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">Email Address <span className="text-red-400">*</span></label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  readOnly
                  className="w-full bg-[#1A233A]/50 border border-white/5 rounded-xl px-4 py-3 text-white/50 cursor-not-allowed"
                  title="Email is fetched from your Google login"
                />
                <p className="text-xs text-white/40 mt-1 mt-2">Email is linked to your Google account.</p>
              </div>

              {/* 3. Contact Number */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">Contact Number <span className="text-red-400">*</span></label>
                <div className="flex bg-[#1A233A] border border-white/10 rounded-xl focus-within:ring-2 focus-within:ring-blue-500/50 focus-within:border-blue-500/50 transition-all overflow-hidden relative">
                  <div className="flex items-center justify-center pl-4 pr-3 border-r border-white/10 bg-white/5 text-white/70 select-none">
                    +91
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full bg-transparent px-4 py-3 text-white placeholder-white/30 focus:outline-none"
                    placeholder="9876543210"
                    pattern="[0-9]*"
                    title="Please enter your 10-digit mobile number"
                    maxLength="10"
                    required
                  />
                </div>
              </div>

              {/* 4. NEET Score */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">NEET Score <span className="text-red-400">*</span></label>
                <input
                  type="number"
                  name="neetScore"
                  value={formData.neetScore}
                  onChange={handleInputChange}
                  className="w-full bg-[#1A233A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                  placeholder="Enter your NEET score (0-720)"
                  min="0"
                  max="720"
                  required
                />
              </div>

              {/* 5. Caste / Category */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">Caste / Category <span className="text-red-400">*</span></label>
                <div className="relative">
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full bg-[#1A233A] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all appearance-none"
                    required
                  >
                    <option value="" disabled className="text-gray-500">Select your category</option>
                    <option value="General" className="text-gray-900 bg-white">General</option>
                    <option value="OBC" className="text-gray-900 bg-white">OBC</option>
                    <option value="SC" className="text-gray-900 bg-white">SC</option>
                    <option value="ST" className="text-gray-900 bg-white">ST</option>
                    <option value="Other" className="text-gray-900 bg-white">Other</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                    <svg className="w-5 h-5 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Submit btn */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full relative group overflow-hidden bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 rounded-xl text-lg font-bold shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="absolute inset-0 bg-white/20 translate-y-[-100%] group-hover:translate-y-[100%] transition-transform duration-500 ease-in-out"></div>
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                       <Spinner /> Submitting...
                    </div>
                  ) : (
                    'Submit Application'
                  )}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentFormPage;
